import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import "./ProjectQuickAccessDock.css";

function ToolIcon({ name }) {
  const paths = {
    script: <><path d="M12 5.5C9 3.5 5 3.5 3 4.5v15c3-1 6-1 9 1 3-2 6-2 9-1v-15c-2-1-6-1-9 1Z" /><path d="M12 5.5v15M6 8h3M15 8h3" /></>,
    assistant: <><path d="M5 4h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H9l-5 3v-3a2 2 0 0 1-2-2V7a3 3 0 0 1 3-3Z" /><path d="M7 9h10M7 13h7" /></>,
    guide: <><circle cx="12" cy="12" r="9" /><path d="m16 8-2.5 5.5L8 16l2.5-5.5L16 8ZM12 3v2M21 12h-2M12 21v-2M3 12h2" /></>,
    optimizer: <><path d="m3 19 9-9 3 3-9 9ZM11 11l3 3M17 2l1.4 3.6L22 7l-3.6 1.4L17 12l-1.4-3.6L12 7l3.6-1.4L17 2ZM5 4v4M3 6h4" /></>,
  };
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}</svg>;
}

function LauncherLabel() {
  return <>
    <svg className="project-quick-tools__grip" width="10" height="16" viewBox="0 0 10 16" fill="currentColor" aria-hidden="true">
      {[4, 8, 12].map((y) => <React.Fragment key={y}><circle cx="3" cy={y} r="1" /><circle cx="7" cy={y} r="1" /></React.Fragment>)}
    </svg>
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <rect x="3.5" y="3.5" width="6" height="6" rx="1.5" /><rect x="14.5" y="3.5" width="6" height="6" rx="1.5" />
      <rect x="3.5" y="14.5" width="6" height="6" rx="1.5" /><rect x="14.5" y="14.5" width="6" height="6" rx="1.5" />
    </svg>
    <span className="project-quick-tools__label">快捷工具</span>
    <svg className="project-quick-tools__chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
  </>;
}

function PlacementIcon({ floating }) {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {floating ? <><path d="M4 4h16v4H4zM12 20V11m-4 4 4-4 4 4" /></> : <><rect x="4" y="8" width="12" height="12" rx="2" /><path d="M13 3h8v8m0-8-10 10" /></>}
  </svg>;
}

const VIEWPORT_GUTTER = 8;
function clampPosition(position, width, height) {
  return {
    x: Math.max(VIEWPORT_GUTTER, Math.min(position.x, window.innerWidth - width - VIEWPORT_GUTTER)),
    y: Math.max(VIEWPORT_GUTTER, Math.min(position.y, window.innerHeight - height - VIEWPORT_GUTTER)),
  };
}

// App still owns all four tools. Placement belongs only to this launcher and resets on route changes.
const ProjectQuickAccessDock = ({
  scriptOpen, assistantOpen, onToggleScript, onToggleAssistant, accentColor,
  guideOpen, optimizerOpen, onOpenGuide, onOpenOptimizer,
}) => {
  const [floating, setFloating] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [slotSize, setSlotSize] = useState(null);
  const [announcement, setAnnouncement] = useState("");
  const isFloating = floating !== null;
  const slotRef = useRef(null);
  const launcherRef = useRef(null);
  const triggerRef = useRef(null);
  const ghostRef = useRef(null);
  const gestureRef = useRef(null);
  const suppressClickRef = useRef(false);
  const restoreFocusRef = useRef(false);

  const rememberSlot = () => {
    const rect = slotRef.current.getBoundingClientRect();
    setSlotSize({ width: rect.width, height: rect.height });
  };
  const redock = () => {
    restoreFocusRef.current = true;
    setFloating(null);
    setSlotSize(null);
    setAnnouncement("快捷工具已放回顶部");
  };
  const detach = () => {
    const rect = launcherRef.current.getBoundingClientRect();
    rememberSlot();
    restoreFocusRef.current = true;
    setFloating(clampPosition({ x: rect.left, y: rect.bottom + 16 }, rect.width + 30, rect.height));
    setAnnouncement("快捷工具已悬浮，可拖动位置，或点击放回顶部");
  };

  useLayoutEffect(() => {
    if (dragging && ghostRef.current && gestureRef.current) {
      const { position } = gestureRef.current;
      ghostRef.current.style.transform = `translate3d(${position.x}px, ${position.y}px, 0)`;
    }
    if (restoreFocusRef.current && !dragging) {
      restoreFocusRef.current = false;
      triggerRef.current?.focus({ preventScroll: true });
    }
  }, [dragging, floating]);

  useEffect(() => {
    return () => gestureRef.current?.cleanup();
  }, []);

  useLayoutEffect(() => {
    if (!isFloating) return undefined;
    const fit = () => {
      const rect = launcherRef.current?.getBoundingClientRect();
      if (!rect) return;
      setFloating((current) => {
        if (!current) return current;
        const next = clampPosition(current, rect.width, rect.height);
        return next.x === current.x && next.y === current.y ? current : next;
      });
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [isFloating]);

  const beginDrag = (event, closeMenu) => {
    // HeadlessUI normally opens on mouse pointerdown. Defer it to click so a drag never flashes the menu.
    event.stopPropagation();
    if (event.button !== 0 || event.isPrimary === false || gestureRef.current) return;
    event.preventDefault();
    suppressClickRef.current = false;
    event.currentTarget.focus({ preventScroll: true });
    const rect = launcherRef.current.getBoundingClientRect();
    const target = event.currentTarget;
    const gesture = {
      pointerId: event.pointerId, startX: event.clientX, startY: event.clientY,
      offsetX: event.clientX - rect.left, offsetY: event.clientY - rect.top,
      width: rect.width, height: rect.height, active: false, returning: false,
      position: { x: rect.left, y: rect.top },
    };
    const update = (pointer) => {
      gesture.position = clampPosition({ x: pointer.clientX - gesture.offsetX, y: pointer.clientY - gesture.offsetY }, gesture.width, gesture.height);
      const slot = slotRef.current.getBoundingClientRect();
      gesture.returning = pointer.clientX >= slot.left - 12 && pointer.clientX <= slot.right + 12
        && pointer.clientY >= slot.top - 12 && pointer.clientY <= slot.bottom + 12;
      slotRef.current.dataset.dropReady = String(gesture.returning);
      if (ghostRef.current) ghostRef.current.style.transform = `translate3d(${gesture.position.x}px, ${gesture.position.y}px, 0)`;
    };
    const move = (pointer) => {
      if (pointer.pointerId !== gesture.pointerId) return;
      if (!gesture.active) {
        if (Math.hypot(pointer.clientX - gesture.startX, pointer.clientY - gesture.startY) < 6) return;
        gesture.active = true;
        closeMenu();
        rememberSlot();
        setDragging(true);
        try { target.setPointerCapture?.(pointer.pointerId); } catch { /* Pointer may have been cancelled by the browser. */ }
      }
      pointer.preventDefault();
      update(pointer);
    };
    const finish = (pointer, cancelled = false) => {
      if (pointer && pointer.pointerId !== gesture.pointerId) return;
      gestureRef.current = null;
      gesture.cleanup();
      if (!gesture.active) return;
      suppressClickRef.current = true;
      restoreFocusRef.current = true;
      if (pointer && !cancelled) update(pointer);
      delete slotRef.current.dataset.dropReady;
      setDragging(false);
      if (cancelled) {
        if (!floating) setSlotSize(null);
        setAnnouncement("已取消移动，快捷工具保持原位");
      } else if (gesture.returning) {
        redock();
      } else {
        setFloating(gesture.position);
        setAnnouncement("快捷工具已悬浮，可拖回顶部虚线区域，或点击放回顶部");
      }
    };
    const up = (pointer) => finish(pointer);
    const cancel = (pointer) => finish(pointer, true);
    const abort = () => finish(null, true);
    const escape = (keyEvent) => {
      if (keyEvent.key !== "Escape") return;
      keyEvent.preventDefault();
      keyEvent.stopPropagation();
      abort();
    };
    gesture.cleanup = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", cancel);
      window.removeEventListener("keydown", escape, true);
      window.removeEventListener("blur", abort);
      window.removeEventListener("resize", abort);
      target.removeEventListener("lostpointercapture", cancel);
      try { if (target.hasPointerCapture?.(gesture.pointerId)) target.releasePointerCapture(gesture.pointerId); } catch { /* Already released. */ }
    };
    gestureRef.current = gesture;
    window.addEventListener("pointermove", move, { passive: false });
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", cancel);
    window.addEventListener("keydown", escape, true);
    window.addEventListener("blur", abort);
    window.addEventListener("resize", abort);
    target.addEventListener("lostpointercapture", cancel);
  };

  const actions = [
    { key: "script", label: "剧本快览", active: scriptOpen, onClick: onToggleScript },
    { key: "assistant", label: "文字助手", active: assistantOpen, onClick: onToggleAssistant },
    { key: "guide", label: "创作指南", active: guideOpen, onClick: onOpenGuide },
    { key: "optimizer", label: "提示词优化", active: optimizerOpen, onClick: onOpenOptimizer },
  ];
  const accentStyle = { "--quick-tools-accent": accentColor || "var(--color-accent-primary)" };

  const launcher = (
    <Menu as="div" ref={launcherRef} className="project-quick-tools" data-floating={Boolean(floating)} data-dragging={dragging}
      style={{ ...accentStyle, ...(floating ? { transform: `translate3d(${floating.x}px, ${floating.y}px, 0)` } : {}) }}>
      {({ close }) => <>
      <MenuButton ref={triggerRef} type="button" className="project-quick-tools__trigger" aria-label="快捷工具" title="点击展开快捷工具，按住拖动可移出或放回顶部"
        onPointerDownCapture={(event) => beginDrag(event, close)}
        onClickCapture={(event) => {
          if (suppressClickRef.current && event.detail > 0) { event.preventDefault(); event.stopPropagation(); }
        }}
        onKeyDownCapture={() => { suppressClickRef.current = false; }}
        onDragStart={(event) => event.preventDefault()}>
        <LauncherLabel />
      </MenuButton>
      {floating ? <button type="button" className="project-quick-tools__return" aria-label="放回顶部" title="放回顶部" onClick={() => { close(); redock(); }}><PlacementIcon floating /></button> : null}
      <MenuItems
        anchor={{ to: "bottom start", gap: 8, padding: 8 }}
        portal
        modal={false}
        className="project-quick-tools__menu"
        style={accentStyle}
        aria-label="项目快捷工具"
        onKeyDown={(event) => event.stopPropagation()}
        onKeyUp={(event) => event.stopPropagation()}
      >
        {actions.map((action) => (
          <MenuItem key={action.key} disabled={typeof action.onClick !== "function"}>
            <button
              type="button"
              className="project-quick-tools__action"
              onClick={action.onClick}
              disabled={typeof action.onClick !== "function"}
              aria-current={action.active ? "true" : undefined}
            >
              <ToolIcon name={action.key} />
              <span>{action.label}</span>
              {action.active ? <span className="project-quick-tools__active" aria-label="已打开" /> : null}
            </button>
          </MenuItem>
        ))}
        <div className="project-quick-tools__separator" role="separator" />
        <MenuItem>
          <button type="button" className="project-quick-tools__action project-quick-tools__placement" onClick={floating ? redock : detach}>
            <PlacementIcon floating={Boolean(floating)} /><span>{floating ? "放回顶部" : "悬浮使用"}</span>
          </button>
        </MenuItem>
      </MenuItems>
      </>}
    </Menu>
  );

  return <div ref={slotRef} className="project-quick-tools__slot" style={{ ...accentStyle, ...(slotSize ? { width: slotSize.width } : {}) }}>
    {floating || dragging ? <button type="button" className="project-quick-tools__dock-target" aria-label="放回顶部" title="点击放回，或把快捷工具拖回此处" onClick={redock}>
      <PlacementIcon floating /><span>放回此处</span>
    </button> : null}
    {floating ? createPortal(launcher, document.body) : launcher}
    {dragging ? createPortal(<div ref={ghostRef} className="project-quick-tools project-quick-tools__ghost" aria-hidden="true" style={accentStyle}>
      <span className="project-quick-tools__trigger"><LauncherLabel /></span>
      {floating ? <span className="project-quick-tools__return"><PlacementIcon floating /></span> : null}
    </div>, document.body) : null}
    <span className="sr-only" role="status">{announcement}</span>
  </div>;
};

export default ProjectQuickAccessDock;
